import type { TFunction } from "i18next";
import type { ChatHistory } from "@webuild-ai/admin/models/components";

import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownPopover,
  DropdownSection,
  DropdownItem,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LLMModel } from "@webuild-ai/agent-platform/models/components";

import { Workflow, useWorkflowStore } from "@/stores/WorkflowStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useChatStore } from "@/stores/ChatStore";
import { getBestForDescription } from "@/utils/modelDescriptions";
import { isSpecializedWorkflow } from "@/utils/workflowUtils";

import { ModelIcon } from "./ModelIcon";

type ModelSelectorPlacement =
  | "bottom-start"
  | "bottom-end"
  | "top-start"
  | "top-end"
  | "left-start"
  | "left-end"
  | "right-start"
  | "right-end";

interface ChatModelSelectorProps {
  position?: ModelSelectorPlacement;
}

/** Providers listed first, in this order; every other provider follows. */
const PROVIDER_ORDER = ["openai", "anthropic", "google"];

/** A model icon naming an image file, which is served from `/models`. */
const IMAGE_FILE_PATTERN = /\.(png|jpe?g|svg)$/;

/** Sort models by providerId: openai, anthropic, google, then the rest in their original order. */
const compareByProvider = (a: LLMModel, b: LLMModel): number => {
  const aIndex = PROVIDER_ORDER.indexOf(a.providerId);
  const bIndex = PROVIDER_ORDER.indexOf(b.providerId);

  // If both are in the list, sort by their order
  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
  // If only a is in the list, it comes first
  if (aIndex !== -1) return -1;
  // If only b is in the list, it comes first
  if (bIndex !== -1) return 1;

  // Otherwise, keep original order
  return 0;
};

/** The model a workflow's agents ask for, falling back to the first model (none if empty). */
const modelForWorkflow = (models: LLMModel[], workflow: Workflow | null): LLMModel | undefined => {
  if (!workflow?.agents) return models[0];

  const agentWithModel = Object.values(workflow.agents).find((agent) => agent.parameters?.model);

  if (!agentWithModel) return models[0];

  const model = models.find((m: LLMModel) => m.id === agentWithModel.parameters.model);

  return model || models[0];
};

/** The model a chat was held with, when it is still on offer. */
const modelFromHistory = (models: LLMModel[], chat: ChatHistory | null): LLMModel | undefined =>
  chat?.modelUsed ? models.find((m: LLMModel) => m.id === chat.modelUsed) : undefined;

/** A copy of the workflow with every agent switched to the given model. */
const withModelOnEveryAgent = (workflow: Workflow, modelId: string): Workflow => ({
  ...workflow,
  agents: Object.fromEntries(
    Object.entries(workflow.agents).map(([id, agent]) => [
      id,
      {
        ...agent,
        parameters: {
          ...agent.parameters,
          model: modelId,
        },
      },
    ]),
  ),
});

/** Image file icons are served from `/models`; any other value is an icon name. */
const modelIconSrc = (icon: string): string =>
  IMAGE_FILE_PATTERN.test(icon) ? `/models/${icon}` : icon;

interface ModelOptionProps {
  model: LLMModel;
  isSelected: boolean;
}

/** One row of the model menu: icon, name, description and the selected tick. */
const ModelOption = ({ model, isSelected }: ModelOptionProps) => (
  <div className="flex items-center gap-2">
    <ModelIcon
      alt={model.name}
      className="text-default-400 shrink-0"
      height={24}
      icon={modelIconSrc(model.icon)}
      width={24}
    />
    <div className="flex flex-col flex-1 min-w-0">
      <span>{model.name}</span>
      <span className="text-tiny text-default-400">{model.description}</span>
    </div>
    {isSelected && (
      <Icon
        className="text-default-foreground shrink-0"
        height={24}
        icon="solar:check-circle-bold"
        width={24}
      />
    )}
  </div>
);

/**
 * The translated strings arrive as props so that every translation call stays in
 * the parent's render, in the order the component has always made them.
 */
interface EnabledModelSelectorProps {
  models: LLMModel[];
  selectedModel: LLMModel;
  position: ModelSelectorPlacement;
  hasMessages: boolean | undefined;
  description: string;
  menuLabel: string;
  sectionTitle: string;
  onSelect: (model: LLMModel) => void;
}

/** Tooltip on hover shows the model description; the dropdown opens on click. */
const EnabledModelSelector = ({
  models,
  selectedModel,
  position,
  hasMessages,
  description,
  menuLabel,
  sectionTitle,
  onSelect,
}: EnabledModelSelectorProps) => (
  <Dropdown data-id="model-selector-dropdown" placement={position}>
    <Tooltip>
      <TooltipTrigger>
        <DropdownTrigger
          className="inline-flex items-center justify-center gap-2 px-3 h-8 text-sm shrink-0 min-w-fit max-w-25 md:flex-1 bg-default-100 hover:bg-default-200 border border-default-300 rounded-full focus-visible:outline-default-400 focus-visible:ring-0"
          data-id="model-selector-button"
          isDisabled={hasMessages}
        >
          <ModelIcon
            alt={selectedModel.name}
            className="text-default-400"
            height={20}
            icon={modelIconSrc(selectedModel.icon)}
            width={20}
          />
          <span className="hidden sm:inline">{selectedModel.name}</span>
          <Icon
            className="text-default-400"
            height={20}
            icon="solar:alt-arrow-down-linear"
            width={20}
          />
        </DropdownTrigger>
      </TooltipTrigger>
      <TooltipContent showArrow className="max-w-70" placement="top">
        {description}
      </TooltipContent>
    </Tooltip>
    <DropdownPopover>
      <DropdownMenu
        aria-label={menuLabel}
        className="px-0 py-4"
        data-id="model-selector-menu"
        style={{ maxHeight: "500px", overflowY: "auto" }}
        variant="ghost"
      >
        <DropdownSection
          className="text-tiny px-2.5"
          data-id="model-selector-section"
          title={sectionTitle}
        >
          {models.map((model) => (
            <DropdownItem
              key={model.id}
              className="text-default-500 data-hovered:text-default-500"
              data-id={`model-item-${model.id}`}
              textValue={model.name}
              onPress={() => onSelect(model)}
            >
              <ModelOption isSelected={model.id === selectedModel.id} model={model} />
            </DropdownItem>
          ))}
        </DropdownSection>
      </DropdownMenu>
    </DropdownPopover>
  </Dropdown>
);

interface DisabledModelSelectorProps {
  selectedModel: LLMModel | null;
  tooltipMessage: string;
}

/** The reason the selector is locked, shown in its tooltip. */
const lockedReason = (isSpecialized: boolean, hasMessages: boolean | undefined, t: TFunction) => {
  if (isSpecialized) return t("disabled-model-workflow");

  return hasMessages ? t("disabled-model-selector") : "";
};

/** Tooltip shows the reason the selector is locked. */
const DisabledModelSelector = ({ selectedModel, tooltipMessage }: DisabledModelSelectorProps) => (
  <Tooltip>
    <TooltipTrigger>
      <Button
        isDisabled
        className="shrink-0 min-w-fit max-w-25 md:flex-1 border! border-default-300! rounded-full disabled:bg-default-100 disabled:text-default-400 disabled:cursor-not-allowed"
        data-id="model-selector-button"
        size="sm"
        variant="ghost"
      >
        <ModelIcon
          alt={selectedModel?.name}
          className="text-default-400"
          height={20}
          icon={(selectedModel ? modelIconSrc(selectedModel.icon) : undefined) as string}
          width={20}
        />
        <span className="hidden sm:inline">{selectedModel?.name}</span>
        <Icon
          className="text-default-400"
          height={20}
          icon="solar:alt-arrow-down-linear"
          width={20}
        />
      </Button>
    </TooltipTrigger>
    <TooltipContent showArrow className="max-w-70" placement="top">
      {tooltipMessage}
    </TooltipContent>
  </Tooltip>
);

const ChatModelSelector = ({ position = "top-end" }: ChatModelSelectorProps) => {
  const { chat } = useChatStore();
  const { getWorkflow, setWorkflow, workflow } = useWorkflowStore();
  const { platformClient } = useWebuildAPIClient();
  const { t } = useTranslation();
  const [models, setModels] = useState<LLMModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);

  useEffect(() => {
    const fetchModels = async () => {
      if (!platformClient) return;
      const availableModels = await platformClient.models.list();

      availableModels.sort(compareByProvider);
      setModels(availableModels);
      // Set initial selected model
      setSelectedModel(modelForWorkflow(availableModels, getWorkflow()) ?? null);
    };

    fetchModels();
  }, [platformClient, getWorkflow]);

  useEffect(() => {
    if (!models.length) return;

    setSelectedModel(
      modelFromHistory(models, chat) ?? modelForWorkflow(models, getWorkflow()) ?? null,
    );
  }, [workflow, models, chat, getWorkflow]);

  const setWorkflowModel = (model: LLMModel) => {
    const current = getWorkflow();

    if (!current?.agents) return;

    setWorkflow(withModelOnEveryAgent(current, model.id));
    setSelectedModel(model);
  };

  // Determine disable reasons
  const hasMessages = chat?.messages && chat?.messages.length > 0;
  const isSpecialized = isSpecializedWorkflow(workflow);
  const isDisabled = isSpecialized || hasMessages;

  // Determine tooltip message for the disabled state
  const tooltipMessage = lockedReason(isSpecialized, hasMessages, t);

  return (
    <div className="flex items-center gap-2">
      {selectedModel && !isDisabled ? (
        <EnabledModelSelector
          description={getBestForDescription(selectedModel.name, t)}
          hasMessages={hasMessages}
          menuLabel={t("Model selector menu")}
          models={models}
          position={position}
          sectionTitle={t("Model")}
          selectedModel={selectedModel}
          onSelect={setWorkflowModel}
        />
      ) : (
        <DisabledModelSelector selectedModel={selectedModel} tooltipMessage={tooltipMessage} />
      )}
    </div>
  );
};

export { ChatModelSelector };
